import React from "react";
import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig, defaultConfigGoodchop } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SupplyWalkEditableCustomControls } from "./custom_controls";
import { getSupplyWalkEditableViewMatrix } from "./get_matrix";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import {
  useApi,
  useCustomer,
  usePerspective,
} from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import {
  SaveApprovedPOsBatchDocument,
  PoActionType,
} from "../../services/api/__generated__/backend_gateway-types";
import { InvalidateMyTransformationCachesDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import { SupplyWalkEditableNestedDetails } from "./nested_details";
import {
  APPROVED_STATUS,
  goodchopExportPlanForStatus,
  runGoodchopExportDownload,
  runGoodchopPdfZipDownload,
} from "./goodchop_export";

// TODO: Implement cascadeRecalculateNested helper when we have a way to modify Arrow table data

export const SupplyWalkEditableContainer = () => {
  const { apiService } = useApi();
  const { perspectiveApiService } = usePerspective();
  const { selectedCustomerId } = useCustomer();
  const { mainRun, lastSuccessfulRun } = React.useContext(VersionedDataContext);
  const { addSystemMessage } = React.useContext(SysMessagesContext);
  const supplyGuard = React.useContext(SupplyWalkEditsContext);

  // Persistent storage is now managed by the SupplyWalkEditsContext

  // Row edit state storage (temporary edits while editing)
  // This replaces the module-level globalRowEditState to properly scope state to this route
  const rowEditStateRef = React.useRef(
    new Map<
      string,
      {
        isEditing: boolean;
        editValues: Record<string, string>;
        committedValues: Record<string, string>;
        originalValues: Record<string, string>;
        listeners: Set<() => void>;
      }
    >()
  ).current;

  // Pending-save queue. Clicks accumulate here; a 20-second interval drains
  // the entire queue with one saveApprovedPOsBatch mutation. This replaces
  // the old per-click round-trip pattern: N clicks now produce one S3 write
  // (read-modify-write of approved_pos.parquet) and one view-transform
  // re-fetch instead of N of each.
  const pendingSavesRef = React.useRef<
    Map<string, { rowData: any; checked: boolean }>
  >(new Map());
  const flushIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const isFlushingRef = React.useRef(false);
  const FLUSH_INTERVAL_MS = 20_000;

  // Clear all edits when run changes to prevent cross-run contamination
  React.useEffect(() => {
    // Clear all edits when run changes
    rowEditStateRef.clear();
  }, [
    mainRun?.runSettings?.runAttemptId,
    lastSuccessfulRun?.runSettings?.runAttemptId,
    rowEditStateRef,
  ]);

  // Build the GraphQL PoActionInput for one queued click. Pure: reads
  // rowData + the supplyGuard edit context, returns null if there's no run
  // to attach to. The actual mutation is sent in batch by flushPendingSaves.
  const buildPoActionForRow = React.useCallback(
    (rowData: any, isApproving: boolean) => {
      const runAttemptId =
        mainRun?.runSettings?.runAttemptId ||
        lastSuccessfulRun?.runSettings?.runAttemptId ||
        "no-run";
      const rowKey = `${rowData.purchase_order_id || "unknown"}-${rowData.sku || "unknown"}-${rowData.location || "unknown"}-${rowData.supplier || "unknown"}-${runAttemptId}`;
      const editData = supplyGuard?.getEdit?.(rowKey);
      const editedValues = editData?.committedValues || {};

      // GoodChop's modification + new-PO suggestions are read-only: approving a
      // row saves the engine-recommended values as-is (no pallet adjustment).
      // The `editedValues` fallbacks below are for other clients' editable
      // "Needs Review" cells (quantity / cost / dates).
      return {
        purchase_order_id: rowData.purchase_order_id,
        sku: rowData.sku,
        sku_name: rowData.sku_name,
        location: rowData.location,
        supplier: rowData.supplier,
        quantity: editedValues.quantity
          ? parseFloat(editedValues.quantity)
          : rowData.quantity,
        total_cost: editedValues.total_cost
          ? parseFloat(editedValues.total_cost)
          : rowData.total_cost,
        unit_cost: editedValues.unit_cost
          ? parseFloat(editedValues.unit_cost)
          : rowData.unit_cost,
        order_placed_date:
          editedValues.order_placed_date || rowData.order_placed_date,
        order_delivery_date:
          editedValues.order_delivery_date || rowData.order_delivery_date,
        pallets: rowData.pallets != null ? Number(rowData.pallets) : null,
        action: isApproving ? PoActionType.Approve : PoActionType.Unapprove,
      };
    },
    [mainRun, lastSuccessfulRun, supplyGuard]
  );

  // Drain the pending-saves queue with ONE batch mutation. Snapshot + clear
  // up front so clicks arriving during the network round-trip land in the
  // next interval rather than the in-flight batch.
  const flushPendingSaves = React.useCallback(async () => {
    if (isFlushingRef.current) return;
    if (pendingSavesRef.current.size === 0) return;
    const runToUse = mainRun || lastSuccessfulRun;
    if (!runToUse) return;

    isFlushingRef.current = true;
    const snapshot = Array.from(pendingSavesRef.current.entries());
    pendingSavesRef.current.clear();

    try {
      const actions = snapshot
        .map(([, payload]) =>
          buildPoActionForRow(payload.rowData, payload.checked)
        )
        .filter((a): a is NonNullable<typeof a> => a != null);

      if (actions.length === 0) return;

      await apiService.getClient().mutate({
        mutation: SaveApprovedPOsBatchDocument,
        variables: {
          runSettings: {
            inputId: runToUse.runSettings.inputId,
            workflowId: runToUse.runSettings.workflowId,
            settingsNameAndVersion: runToUse.runSettings.settingsNameAndVersion,
            runAttemptId: runToUse.runSettings.runAttemptId,
          },
          actions,
        },
      });

      // Drop the perspective server's cached transform results for this run so
      // the next subscribe re-runs against the just-written approved_pos. The
      // browser calls the perspective server directly (its public ALB) — the
      // in-VPC backend gateway can't reach it, which is why the old server-side
      // invalidation silently no-op'd ("fetch failed" / APP-299). Best-effort:
      // the save already persisted; a stale cache otherwise self-heals on TTL.
      try {
        await perspectiveApiService.getClient().mutate({
          mutation: InvalidateMyTransformationCachesDocument,
          variables: {
            runSettings: {
              inputId: runToUse.runSettings.inputId,
              workflowId: runToUse.runSettings.workflowId,
              settingsNameAndVersion:
                runToUse.runSettings.settingsNameAndVersion,
              runAttemptId: runToUse.runSettings.runAttemptId,
            },
          },
        });
      } catch (invalidateErr) {
        console.warn(
          "failed to invalidate perspective transformation caches",
          invalidateErr
        );
      }
      supplyGuard?.bumpReloadVersion?.();
    } catch (error) {
      // Restore the snapshot to the head of the queue so clicks aren't lost
      // — the next interval (or unmount flush) will retry them.
      for (const [rowKey, payload] of snapshot) {
        if (!pendingSavesRef.current.has(rowKey)) {
          pendingSavesRef.current.set(rowKey, payload);
        }
      }
      const errorMsg =
        (error as Error)?.message || String(error) || "Unknown error";
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: `Failed to save approved POs: ${errorMsg}`,
      });
    } finally {
      isFlushingRef.current = false;
    }
  }, [
    mainRun,
    lastSuccessfulRun,
    apiService,
    perspectiveApiService,
    supplyGuard,
    buildPoActionForRow,
    addSystemMessage,
  ]);

  // 20-second batch flush interval. Replaces the old per-click 1-second
  // debounce + per-row mutation: clicks accumulate locally and one batch
  // mutation lands every 20s, producing one S3 write + one view-transform
  // re-fetch per interval regardless of click volume.
  React.useEffect(() => {
    flushIntervalRef.current = setInterval(() => {
      void flushPendingSaves();
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
      // Best-effort flush of anything pending when the component unmounts so
      // the user doesn't lose clicks on navigation.
      void flushPendingSaves();
    };
  }, [flushPendingSaves]);

  // Belt-and-suspenders: also flush when the tab becomes hidden so closing
  // the tab / switching apps doesn't drop pending clicks beyond the 20s
  // window. The unmount flush above handles in-app navigation.
  React.useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") {
        void flushPendingSaves();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [flushPendingSaves]);

  // Handle checkbox toggle: apply optimistic state, then enqueue a save.
  const handleApprovedToggle = React.useCallback(
    (rowKey: string, rowData: any, checked: boolean) => {
      // Update the edit context immediately for navigation guard
      if (supplyGuard?.setRowApproved) {
        supplyGuard.setRowApproved(rowKey, checked);
      }

      // Update approved row count in real-time
      if (checked) {
        supplyGuard?.incrementApprovedRowCount?.();
        // Record the source bucket so the status-bar tab counts decrement
        // immediately on click. The DUMMY-row counts baked into the parquet
        // only update after save → server cache invalidate → re-subscribe →
        // fresh transform — a multi-second round trip the user shouldn't see.
        // Skip when source is already "Approved" (unapprove-then-approve on
        // the Approved tab) since there's no source bucket to decrement.
        const sourceStatus = String(rowData?.status ?? "");
        if (sourceStatus && sourceStatus !== "Approved") {
          supplyGuard?.recordPendingApproval?.(rowKey, sourceStatus);
        }
      } else {
        supplyGuard?.decrementApprovedRowCount?.();
        // Drop the optimistic delta if we created one this session. We can't
        // restore the source bucket count for a row that was already approved
        // before page load (the original status isn't tracked) — those refresh
        // on the next data update.
        supplyGuard?.clearPendingApproval?.(rowKey);
      }

      // Last click wins for a given row in the current 20-second window.
      // The recurring flush interval picks the entry up on its next tick.
      pendingSavesRef.current.set(rowKey, { rowData, checked });
    },
    [supplyGuard]
  );

  const CustomControlsWithApprovedCount = React.useCallback((props: any) => {
    return <SupplyWalkEditableCustomControls {...props} />;
  }, []);

  const isGoodchop = selectedCustomerId === "goodchop";

  // GoodChop's CSV exports must match the old-stack column shape so planner
  // tooling downstream doesn't break. The default Download path serializes
  // the UI's arrow table (with __expand / __actions / etc.); for goodchop we
  // route to dedicated `goodchop_*_export` view-transforms that produce the
  // exact 12- and 18-column formats the old stack emitted. Other customers
  // continue to use the default in-memory export path.
  const customHandleDownload = React.useCallback(
    (params: any) => {
      if (!isGoodchop) {
        throw new Error("not goodchop — fall through to default export");
      }

      const statusFilter = params?.filters?.find?.(
        (f: any) => Array.isArray(f) && f[0] === "status"
      );
      const currentStatus =
        statusFilter && Array.isArray(statusFilter[2])
          ? statusFilter[2][0]
          : null;

      const exportPlan = goodchopExportPlanForStatus(currentStatus);
      if (!exportPlan) {
        throw new Error("active tab has no goodchop export — fall through");
      }

      const runSettings =
        mainRun?.runSettings ?? lastSuccessfulRun?.runSettings;
      if (!runSettings) {
        throw new Error("no run selected — fall through to default export");
      }

      const runCreatedAt =
        (mainRun ?? lastSuccessfulRun)?.createdAt ?? null;

      // Run exports sequentially so the perspective server's transform cache
      // hits populate cleanly and the user gets predictable download ordering.
      // CSV only. The vendor-facing PO PDFs are a separate button/gesture
      // (customHandleDownloadPdf) so the two artifacts pull independently.
      (async () => {
        for (const e of exportPlan.exports) {
          await runGoodchopExportDownload({
            apolloClient: perspectiveApiService.getClient(),
            transformId: e.transformId,
            runSettings,
            filenamePrefix: e.filenamePrefix,
            runCreatedAt,
          });
        }
      })().catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: `Export failed: ${message}`,
        });
      });
    },
    [
      isGoodchop,
      mainRun,
      lastSuccessfulRun,
      perspectiveApiService,
      addSystemMessage,
    ]
  );

  // Approved-tab companion to the CSV Download: renders one vendor-facing PO PDF
  // per approved order (backend-gateway) and downloads them as a zip. Returns the
  // promise so the toolbar's PDF button can spin until the zip resolves.
  const customHandleDownloadPdf = React.useCallback(
    async (params: any) => {
      if (!isGoodchop) return;

      const statusFilter = params?.filters?.find?.(
        (f: any) => Array.isArray(f) && f[0] === "status"
      );
      const currentStatus =
        statusFilter && Array.isArray(statusFilter[2])
          ? statusFilter[2][0]
          : null;
      if (currentStatus !== APPROVED_STATUS) return;

      const runSettings =
        mainRun?.runSettings ?? lastSuccessfulRun?.runSettings;
      if (!runSettings) return;

      try {
        await runGoodchopPdfZipDownload({
          apolloClient: apiService.getClient(),
          runSettings,
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: `PDF export failed: ${message}`,
        });
      }
    },
    [isGoodchop, mainRun, lastSuccessfulRun, apiService, addSystemMessage]
  );

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={
        {
          datasetName: "po_plan_edit",
          transformId: isGoodchop
            ? "supply_walk_editable_goodchop"
            : "supply_walk_editable",
          folder: "outputs",
          title: "Purchase Order Inbox (Beta)",
          customHandleDownload: isGoodchop ? customHandleDownload : undefined,
          customHandleDownloadPdf: isGoodchop
            ? customHandleDownloadPdf
            : undefined,
          dateHierarchyColumnFilter: null,
          excludedSegmentationsFilter: [],
          measureColumnFilter: null,
          excludedFilterColumnFilter: [],
          columnHeaderMap: isGoodchop
            ? {
                purchase_order_id: "PO Number",
                sku: "SKU",
                sku_name: "Description",
                location: "Location",
                supplier: "Supplier",
                quantity: "Quantity",
                order_placed_date: "PO Date",
                order_delivery_date: "ETA",
                unit_cost: "Unit Cost",
                total_cost: "Total Cost",
                status: "Status",
                pallets: "Pallets",
                modification_reason: "Reason",
                targeted_po_arrival: "Target Arrival",
                targeted_po_placement: "Target Placement",
                baseline_pallets: "Original Pallets",
                baseline_quantity: "Original Quantity",
              }
            : {
                purchase_order_id: "PO ID",
                sku: "SKU",
                sku_name: "SKU name",
                reporting_attribution: "Reporting Attribute",
                order_placed_date: "PO day",
                order_delivery_date: "ETA day",
                total_cost: "total cost",
                unit_cost: "unit cost",
              },
          showControls: [
            PivotControl.Download,
            ...(isGoodchop ? [PivotControl.DownloadPdf] : []),
            PivotControl.ShowSettings,
            PivotControl.StoreAsDefault,
          ],
          customControlPanel: CustomControlsWithApprovedCount,
          paramsToTransformArgs: (
            params: DatePivotParams
          ): Record<string, any> => {
            return {
              date_field: params.dateHierarchy,
              group_by_fields: [],
              filters: params.filters,
            };
          },
          tableType: "flat",
          getMatrix: (params: any) => {
            // Try multiple ways to get the current status
            let currentStatus = "";

            // Method 1: Check params.filters
            if (params.filters) {
              const statusFilter = params.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            // Method 2: Check if status is directly in params
            if (!currentStatus && params.status) {
              currentStatus = params.status;
            }

            // Method 3: Check viewParams.filters
            if (!currentStatus && params.viewParams?.filters) {
              const statusFilter = params.viewParams.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            return getSupplyWalkEditableViewMatrix({
              ...params,
              viewParams: { ...(params?.viewParams || {}) },
              mainRun,
              lastSuccessfulRun,
              NestedDetailsComponent: SupplyWalkEditableNestedDetails,
              currentStatus,
              rowEditStateRef,
              onApprovedToggle: handleApprovedToggle,
              supplyGuard,
              isGoodchop,
            });
          },
        } as any
      }
      defaultConfig={isGoodchop ? defaultConfigGoodchop : defaultConfig}
    />
  );
};
